import { useEffect } from 'react';
import { useStore } from '../store/StoreContext.jsx';
import { getOwnerNameOptions, getBeneficiaryNameOptions } from '../store/selectors.js';
import { genUid } from '../store/uid.js';
import DataTable from './DataTable.jsx';
import Field from './Field.jsx';
import { SectionShell } from './FormSection.jsx';
import { SF_TEXT_LIMITS } from '../schema/fieldLimits.js';

const RE_CHECK_DEFS = [
  { key: 'ownHome',       label: 'Own your home?',         desc: 'Primary Residence' },
  { key: 'secondaryHome', label: 'Secondary home(s)?',     desc: 'Secondary Residence', legacyDescs: ['Secondary Home'] },
  { key: 'additionalRE',  label: 'Additional real estate?', desc: 'Additional Real Estate' },
];

// The account types every household is asked about. They are always shown, with
// no checkbox to tick first: advisors did not like "having to check the boxes on
// the different account types before entering the info" (Jake relaying advisor
// feedback, 2026-08-13), and the checkbox was getting in the way of the common
// case. Each carries a "None" marker instead, which records that the question
// WAS asked and the answer was none. Cash & CD's joined this group on 8/14 —
// nearly every household has some, so it belongs with the always-asked three.
const INV_CORE_DEFS = [
  { key: 'taxDeferred', label: 'Tax-Deferred Retirement' },
  { key: 'roth',        label: 'Tax-Free Roth' },
  { key: 'taxable',     label: 'Taxable Non-Retirement' },
  { key: 'cashCd',      label: "Cash & CD's" },
];

// The less common types, still opt-in. These stay checkboxes and sit below the
// core sections under "Add an Account Type", so the default view is the four
// sections above rather than six empty prompts.
const INV_EXTRA_DEFS = [
  { key: 'plan529',     label: '529 Plans' },
  { key: 'hsa',         label: 'HSA' },
];

// Does this table hold anything an advisor typed? Internal bookkeeping keys
// (_uid, _autoKey, ...) do not count. Used to block the "None" marker while a
// section has accounts in it, so the record can never assert both.
function tableHasData(rows) {
  if (!Array.isArray(rows)) return false;
  return rows.some(r => r && Object.keys(r).some(k => {
    if (k.startsWith('_')) return false;
    const v = r[k];
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    return true;
  }));
}

const RE_DATA_KEYS = [
  'marketValue', 'remainingLoan', 'interestRate', 'term', 'payment',
  'yearAcquired', 'costBasis', 'ownership', 'incomeEBT',
];

function rowHasData(row) {
  return RE_DATA_KEYS.some(k => {
    const v = row?.[k];
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    return true;
  });
}

// The "None" marker. A checkbox, not a button: as a bare pill it read as a
// status badge that would light up on its own rather than something to click
// (Jimmy, 2026-08-14). Same control in both states — ticked it collapses the
// section, unticked it brings the table back — so there is one thing to learn
// rather than a "None" button and a separate "Undo" button.
function NoneCheck({ def, marked, hasData, onChange }) {
  const title = hasData
    ? 'Clear the accounts below before marking this as none.'
    : marked
      ? `Untick to show the ${def.label} table again`
      : `Record that you asked about ${def.label} and the client has none`;
  return (
    <label
      className={'none-check' + (marked ? ' is-on' : '') + (hasData ? ' is-disabled' : '')}
      title={title}
    >
      <input
        type="checkbox"
        className="none-toggle"
        data-none-for={def.key}
        checked={marked}
        disabled={hasData}
        title={title}
        onChange={e => onChange(e.target.checked)}
      />
      None
    </label>
  );
}

function CheckboxGroup({ defs, checks, onToggle, children }) {
  return (
    <div className="choice-group" style={{ marginBottom: 10 }}>
      {defs.map(d => (
        <label
          key={d.key}
          className={'choice' + (checks[d.key] ? ' is-on' : '')}
        >
          <input
            type="checkbox"
            checked={!!checks[d.key]}
            onChange={e => onToggle(d.key, e.target.checked)}
          />
          {d.label}
        </label>
      ))}
      {/* Extra choices that share the row but aren't in `defs` (e.g. Renting,
          which must not drive real-estate row reconciliation). */}
      {children}
    </div>
  );
}

export default function AssetsSection({ section, num }) {
  const { data, update } = useStore();
  const ownershipOptions = getOwnerNameOptions(data);
  // Beneficiary / FBO fields also suggest the children & grandchildren by name.
  const beneficiaryOptions = getBeneficiaryNameOptions(data);
  const checks = data._assetChecks || {};

  const setCheck = (key, value) => update(`_assetChecks.${key}`, value);

  // "Asked, client has none" markers for the three core investment sections,
  // stored alongside the account tables. Same shape as insurance.noneOf.
  const noneOf = Array.isArray(data?.assets?.noneOf) ? data.assets.noneOf : [];
  const setNone = (key, on) => {
    const next = on
      ? [...noneOf.filter(k => k !== key), key]
      : noneOf.filter(k => k !== key);
    update('assets.noneOf', next);
  };

  // Owning a home and renting are mutually exclusive. Checking "Own your home?"
  // clears (and greys out) "Renting?" so no phantom rent expense lingers.
  const setReCheck = (key, value) => {
    setCheck(key, value);
    if (key === 'ownHome' && value) setCheck('renting', false);
  };

  // Reconcile real-estate auto-rows with checkbox state.
  // For each checked box: ensure a row exists with that _autoKey (description tied to box).
  // For each unchecked box: remove the row IF it has no user data beyond description.
  useEffect(() => {
    const existing = Array.isArray(data?.assets?.realEstate) ? data.assets.realEstate : [];
    let merged = existing.filter(r => r != null);
    let changed = false;

    RE_CHECK_DEFS.forEach(def => {
      const idx = merged.findIndex(r => r?._autoKey === def.key);
      if (checks[def.key]) {
        if (idx === -1) {
          merged.push({ _uid: genUid(), _autoKey: def.key, description: def.desc });
          changed = true;
        } else if (!String(merged[idx].description ?? '').trim()) {
          // Backfill the default label only when the description is blank.
          // A user-renamed description (e.g. "Condo in Aspen") is preserved —
          // the row stays tied to the checkbox by _autoKey, not the text.
          merged[idx] = { ...merged[idx], description: def.desc };
          changed = true;
        }
      } else if (idx !== -1 && !rowHasData(merged[idx])) {
        merged.splice(idx, 1);
        changed = true;
      }
    });

    if (changed) update('assets.realEstate', merged);
  }, [checks.ownHome, checks.secondaryHome, checks.additionalRE, data?.assets?.realEstate, update]);

  // `detail: true` marks the columns that leave the row for the per-row detail
  // panel when the table is squeezed (R2). The primary row keeps identity +
  // money: Description, Market Value, Remaining Loan, Payment, Ownership.
  const reColumns = [
    { key: '_autoKey',      label: '',               hidden: true },
    // Widths. A fixed-layout table needs one fluid column, and on 2026-08-17
    // it was Description, which is the wrong one: a name column has a natural
    // maximum and swelled into a runway (424px on Jimmy's normal screen). The
    // MONEY columns are the fluid ones now: they have a natural minimum (room
    // for seven figures) and no harm in growing. So Description is capped in
    // pixels, the short numerics are pinned in pixels, and Market Value /
    // Remaining Loan / Payment split whatever is left.
    { key: 'description',   label: 'Description',    colClass: 'col-desc' },
    { key: 'marketValue',   label: 'Market Value',   type: 'currency' },
    { key: 'remainingLoan', label: 'Remaining Loan', type: 'currency' },
    // Pixel hints, not col-narrow: a percentage share grows with the table, so
    // on a 1920 monitor an interest rate got 150px and Description clipped.
    { key: 'interestRate',  label: 'Int. Rate',      type: 'percent', colClass: 'col-short', detail: true },
    { key: 'term',          label: 'Term',           colClass: 'col-short', labelInfo: 'In years', detail: true, numeric: true, maxLength: SF_TEXT_LIMITS['PQ_Asset__c.Term__c'] },
    { key: 'payment',       label: 'Payment (P&I)',  type: 'currency', labelInfo: 'Monthly' },
    // The order a newer advisor asks the questions in (Joe Anderson, 10/21):
    // the loan, then what the property earns, then when it was bought, what
    // it cost and whose it is. Income sat last before, and the eye went to
    // ownership before income.
    //
    // A primary residence earns no rent, so the income box is greyed on that
    // row and the Tab key skips it. An old record that does carry a figure
    // there keeps showing it, so nothing already entered goes out of sight.
    {
      key: 'incomeEBT',     label: 'Income (EBT)',   type: 'currency', detail: true, labelInfo: 'Monthly rental/property income before tax — flows into Other Income as an annual amount (×12).',
      appliesWhen: row => row?._autoKey !== 'ownHome' || String(row?.incomeEBT ?? '').trim() !== '',
      appliesHint: 'A primary residence has no rental income.',
    },
    { key: 'yearAcquired',  label: 'Year Acquired',  colClass: 'col-short', detail: true, numeric: true, maxLength: SF_TEXT_LIMITS['PQ_Asset__c.Year_Acquired__c'] },
    { key: 'costBasis',     label: 'Cost Basis',     type: 'currency', detail: true, labelInfo: 'What was originally paid for the property (purchase price plus improvements) — used for capital-gains planning.' },
    { key: 'ownership',     label: 'Ownership',      type: 'datalist', options: ownershipOptions, colClass: 'col-owner' },
  ];

  const realEstateRows = Array.isArray(data?.assets?.realEstate) ? data.assets.realEstate : [];
  const anyREChecked = RE_CHECK_DEFS.some(d => checks[d.key]);
  const showRETable = anyREChecked || realEstateRows.length > 0;

  // Common account types as picker suggestions (UAT: Eugene, 7/8). A datalist,
  // not a hard dropdown — free typing still allowed, matching the Owner-field
  // convention. Keep the keywords bucket-compatible with fromSalesforce's
  // type-based fallback (401/403/457/ira/sep/simple/pension/deferred/annuit).
  const TAX_DEFERRED_TYPES = [
    '401(k)', '403(b)', '457(b)', 'Traditional IRA', 'Rollover IRA',
    // Inherited IRA requested by Jeff Wille in the pilot channel 2026-07-31;
    // common enough to meet in a first appointment. Contains "ira", so
    // fromSalesforce's type-based bucketing still lands it in tax-deferred.
    'Inherited IRA',
    'SEP IRA', 'SIMPLE IRA', 'Pension', 'Deferred Comp', 'Annuity',
  ];
  const ROTH_TYPES = ['Roth IRA', 'Roth 401(k)', 'Roth 403(b)'];
  // Registration / contract types for taxable (non-retirement) accounts — the
  // common brokerage registrations plus annuity types (UAT: Eugene, 7/8). This
  // repurposes the old free-text "Variable / Fixed" column: the stored key stays
  // `variableFixed`, so it still round-trips to Salesforce with no schema change
  // — only the label and picker options changed. A datalist, so free typing
  // still works for anything not listed.
  // The year the account was opened. Asked on Roth accounts for the five-year
  // rule, which runs from the first Roth the client ever funded (Joe Anderson,
  // 10/21), and on taxable accounts beside the annuity issue date. Text(4) in
  // Salesforce like Year Acquired, so it is typed the same way.
  const YEAR_ESTABLISHED_COL = {
    key: 'yearEstablished', label: 'Year Established', colClass: 'col-short', detail: true, numeric: true,
    maxLength: SF_TEXT_LIMITS['PQ_Asset__c.Year_Established__c'],
    labelInfo: 'The year the account was opened. For a Roth, the year of the first Roth contribution starts the five-year clock.',
  };
  const TAXABLE_TYPES = [
    'Individual', 'Joint (JTWROS)', 'Community Property', 'Trust',
    'Custodial (UTMA/UGMA)', 'Transfer on Death (TOD)', 'Managed Account',
    'Variable Annuity', 'Fixed Annuity',
  ];

  // A client who maxes out an account thinks "the max", not a dollar figure,
  // and the limit moves every year. Ticked, the push sends the contribution as
  // Maximum and eMoney keeps it at the limit (P40). Retirement and HSA accounts
  // only: the taxable account has no limit to max out.
  const MAX_CONTRIBUTION_COL = { key: 'maxContribution', label: 'Maxes Out', type: 'checkbox', detail: true };

  const investmentTables = {
    taxDeferred: {
      key: 'taxDeferred', title: 'Tax-Deferred Retirement', titleClass: 'tone-tax-deferred', showTotals: true,
      columns: [
        { key: 'custodian', label: 'Custodian', colClass: 'col-wide' },
        { key: 'marketValue', label: 'Market Value', type: 'currency' },
        { key: 'personalAdditions', label: 'Personal Additions', type: 'currency', labelInfo: 'Yearly', detail: true },
        MAX_CONTRIBUTION_COL,
        { key: 'companyMatch', label: 'Company Match', type: 'currency', detail: true },
        { key: 'type', label: 'Type', type: 'datalist', options: TAX_DEFERRED_TYPES, detail: true, maxLength: SF_TEXT_LIMITS['PQ_Asset__c.Type__c'] },
        { key: 'ownership', label: 'Ownership', type: 'datalist', options: ownershipOptions },
        { key: 'beneficiary', label: 'Beneficiary', type: 'datalist', options: beneficiaryOptions, detail: true },
      ],
    },
    roth: {
      key: 'roth', title: 'Tax-Free Roth', titleClass: 'tone-tax-free', showTotals: true,
      columns: [
        { key: 'custodian', label: 'Custodian', colClass: 'col-wide' },
        { key: 'marketValue', label: 'Market Value', type: 'currency' },
        { key: 'personalAdditions', label: 'Personal Additions', type: 'currency', labelInfo: 'Yearly', detail: true },
        MAX_CONTRIBUTION_COL,
        { key: 'companyMatch', label: 'Company Match', type: 'currency', detail: true },
        { key: 'type', label: 'Type', type: 'datalist', options: ROTH_TYPES, detail: true, maxLength: SF_TEXT_LIMITS['PQ_Asset__c.Type__c'] },
        YEAR_ESTABLISHED_COL,
        { key: 'ownership', label: 'Ownership', type: 'datalist', options: ownershipOptions },
        { key: 'beneficiary', label: 'Beneficiary', type: 'datalist', options: beneficiaryOptions, detail: true },
      ],
    },
    taxable: {
      key: 'taxable', title: 'Taxable Non-Retirement', titleClass: 'tone-taxable', showTotals: true,
      columns: [
        { key: 'custodian', label: 'Custodian', colClass: 'col-wide' },
        { key: 'marketValue', label: 'Market Value', type: 'currency' },
        { key: 'personalAdditions', label: 'Personal Additions', type: 'currency', labelInfo: 'Yearly', detail: true },
        { key: 'costBasis', label: 'Cost Basis', type: 'currency', labelInfo: 'Total amount originally invested — used to estimate unrealized gains for tax planning.', detail: true },
        { key: 'ownership', label: 'Ownership', type: 'datalist', options: ownershipOptions },
        { key: 'variableFixed', label: 'Type', type: 'datalist', options: TAXABLE_TYPES, labelInfo: 'Account registration (Individual, Joint, Trust…) or, for annuities, the contract type (Variable / Fixed).', detail: true, maxLength: SF_TEXT_LIMITS['PQ_Asset__c.Variable_Fixed__c'] },
        YEAR_ESTABLISHED_COL,
        // Only an annuity has an issue date, and the eMoney push sends it as
        // the contract date, so it keeps its name and its full date; on every
        // other account the box is greyed, which is what Joe read as "this
        // should be Year Established" on 10/21. A date already on the row
        // stays in view whatever the type says.
        {
          key: 'issueDate', label: 'Issue Date', type: 'date', dateRange: 'historical', labelInfo: 'For annuities: the date the contract was issued — drives surrender-period and payout timing.', detail: true,
          appliesWhen: row => /annuity/i.test(String(row?.variableFixed ?? '')) || String(row?.issueDate ?? '').trim() !== '',
          appliesHint: 'Only an annuity has an issue date.',
        },
        { key: 'beneficiary', label: 'Beneficiary', type: 'datalist', options: beneficiaryOptions, detail: true },
      ],
    },
    cashCd: {
      // Same dot as the other core sections now that it sits among them.
      // Cash is taxable money — Present mode already folds it into the taxable
      // total — so it shares that tone rather than getting a fourth one.
      key: 'cashCd', title: "Cash & CD's", titleClass: 'tone-taxable', showTotals: true,
      columns: [
        { key: 'description', label: 'Description', colClass: 'col-wide' },
        { key: 'marketValue', label: 'Market Value', type: 'currency' },
        { key: 'interestRate', label: 'Interest Rate', type: 'percent', colClass: 'col-narrow', detail: true },
        { key: 'ownership', label: 'Ownership', type: 'datalist', options: ownershipOptions },
      ],
    },
    plan529: {
      // 529-specific columns (UAT: Eugene, 7/8): FBO (who the plan is for) and
      // the sponsoring state (drives state-tax treatment) replace the
      // interest-rate column, which never fit this account type. FBO rides the
      // existing beneficiary payload field; `state` is a new payload key —
      // Salesforce field pending with Srivani, until then it round-trips only
      // once that field lands.
      key: 'plan529', title: '529 Plans', showTotals: true,
      columns: [
        { key: 'description', label: 'Plan Name', colClass: 'col-wide' },
        { key: 'beneficiary', label: 'FBO', type: 'datalist', options: beneficiaryOptions, labelInfo: 'For the benefit of — the child/person this plan is for.' },
        { key: 'marketValue', label: 'Market Value', type: 'currency' },
        { key: 'custodian', label: 'Custodian', labelInfo: 'The plan provider / firm holding the account (e.g. Vanguard, Fidelity, ScholarShare).', detail: true },
        { key: 'state', label: 'State', colClass: 'col-narrow', labelInfo: 'The sponsoring state — matters for state-tax deductions.', detail: true, maxLength: SF_TEXT_LIMITS['PQ_Asset__c.State__c'] },
        { key: 'ownership', label: 'Owner', type: 'datalist', options: ownershipOptions, labelInfo: 'Who owns the account (may be a grandparent or other relative, not the client).' },
      ],
    },
    hsa: {
      // Mirrors the Roth column set (UAT: Eugene, 7/8) — HSAs are investment
      // accounts too: they take yearly contributions, employer money, and can
      // hold invested positions. This also closes the long-standing "HSA has
      // no company match field" workaround from the limitations doc.
      key: 'hsa', title: 'HSA Accounts', showTotals: true,
      columns: [
        { key: 'custodian', label: 'Custodian', colClass: 'col-wide' },
        { key: 'marketValue', label: 'Market Value', type: 'currency' },
        { key: 'personalAdditions', label: 'Personal Additions', type: 'currency', labelInfo: 'Yearly', detail: true },
        MAX_CONTRIBUTION_COL,
        { key: 'companyMatch', label: 'Company Match', type: 'currency', detail: true },
        { key: 'type', label: 'Type', detail: true, maxLength: SF_TEXT_LIMITS['PQ_Asset__c.Type__c'] },
        { key: 'ownership', label: 'Ownership', type: 'datalist', options: ownershipOptions },
        { key: 'beneficiary', label: 'Beneficiary', type: 'datalist', options: beneficiaryOptions, detail: true },
      ],
    },
  };

  return (
    <SectionShell section={section} num={num}>
      {/* --- Real Estate --- */}
      <div className="subhead">Real Estate</div>
      {/* Renting sits inline with the ownership checks but is passed as a child,
          not a RE_CHECK_DEFS entry: a renter owns no property, so it must never
          create a real-estate row. Its monthly rent flows to Annual Expenses
          (x12) via the auto-source sync in Taxes & Expenses. */}
      <CheckboxGroup defs={RE_CHECK_DEFS} checks={checks} onToggle={setReCheck}>
        <label
          className={'choice' + (checks.renting ? ' is-on' : '') + (checks.ownHome ? ' is-disabled' : '')}
        >
          <input
            type="checkbox"
            checked={!!checks.renting}
            disabled={!!checks.ownHome}
            onChange={e => setCheck('renting', e.target.checked)}
          />
          Renting?
        </label>
      </CheckboxGroup>
      {checks.renting && (
        <div className="grid">
          <Field
            field={{ key: 'rentMonthly', label: 'Monthly Rent', type: 'currency', width: 'medium', labelInfo: 'Added to Annual Expenses as rent × 12' }}
            value={data?.assets?.rentMonthly}
            onChange={val => update('assets.rentMonthly', val)}
          />
        </div>
      )}
      {showRETable && (
        <DataTable
          sectionId="assets"
          tableDef={{
            key: 'realEstate',
            title: '',
            ariaTitle: 'Real Estate',
            showTotals: true,
            // Every other asset table is reorderable; this one was simply never
            // flagged, which is why drag-and-drop "worked everywhere except real
            // estate" (multiple advisors, 2026-08-03).
            reorderable: true,
            // When squeezed, the detail columns drop to an always-visible
            // labeled line under each row instead of hiding behind a chevron.
            // Real Estate is the widest table and the one advisors run at half
            // width in live meetings; fields behind the chevron were reported
            // as hidden, and unlabeled compressed layouts as unreadable
            // (David Kleinman / Jeff Wille, 8/13).
            detailSubRow: true,
            // The width at which all ten columns fit on one line with the
            // five money columns at a 104px floor — what a right-aligned
            // "$ 1,725,000" needs with the $ clear of the digits, since a
            // seven-figure value clipping to "$1,725,0…" is the misread the
            // codebase already guards against: control gutters 83 +
            // description 156 + ownership 118 + three short × 68 + five money
            // × 104 = 1081. Arithmetic on the pixel hints, not a guess. Jimmy's
            // normal screen (1920 wide, both panels pinned) is a 1088px
            // container, and he wants one line there; the name and short
            // columns were trimmed to exactly what they hold to make it fit.
            inlineMinPx: 1081,
            columns: reColumns,
          }}
          noSeed
          onRemoveRow={row => {
            // Removing a checkbox-created property unticks its checkbox, so the
            // reconcile effect doesn't immediately re-add the row (John, 7/10).
            if (row?._autoKey) setCheck(row._autoKey, false);
          }}
        />
      )}

      {/* --- Business and Other --- */}
      <div className="subhead">Business and Other</div>
      <DataTable
        sectionId="assets"
        tableDef={{
          key: 'businessOther',
          title: '',
          ariaTitle: 'Business and Other',
          showTotals: true,
          reorderable: true,
          columns: [
            { key: 'description', label: 'Description' },
            { key: 'marketValue', label: 'Market Value', type: 'currency' },
            { key: 'costBasis', label: 'Cost Basis', type: 'currency' },
            { key: 'ownership', label: 'Ownership', type: 'datalist', options: ownershipOptions },
          ],
        }}
      />

      {/* --- Investment Accounts --- */}
      <div className="subhead">Investment Accounts</div>

      {/* The core types are always open. A "None" checkbox beside each title
          records that the question was asked and the answer was none, which an
          absent table cannot say on its own. */}
      {INV_CORE_DEFS.map(def => {
        const hasData = tableHasData(data?.assets?.[def.key]);
        // Accounts win over a stale marker. A record can only arrive holding
        // both from outside this screen (edited in Salesforce, or a second tab),
        // and toSalesforce drops the contradicted marker on save — so showing
        // the table matches what will be stored, and the advisor can still see
        // the accounts instead of being locked out behind a collapsed stub.
        const marked = noneOf.includes(def.key) && !hasData;
        const none = (
          <NoneCheck
            def={def}
            marked={marked}
            hasData={hasData}
            onChange={on => setNone(def.key, on)}
          />
        );
        if (marked) {
          return (
            <div className="section-none" key={def.key}>
              <span className="section-none-title">{def.label}</span>
              <span className="section-none-note">Asked, client has none</span>
              {none}
            </div>
          );
        }
        return (
          <DataTable
            key={def.key}
            sectionId="assets"
            tableDef={{
              ...investmentTables[def.key],
              reorderable: true,
              // Same treatment as Real Estate: when squeezed, the detail
              // columns drop to a labeled line under each row rather than
              // hiding behind a chevron (Jimmy, 2026-08-17).
              detailSubRow: true,
              headerAction: none,
            }}
          />
        );
      })}

      {/* The opt-in types sit below the core sections, not above everything. */}
      <div className="subhead" style={{ marginTop: 4 }}>Add an Account Type:</div>
      <CheckboxGroup defs={INV_EXTRA_DEFS} checks={checks} onToggle={setCheck} />
      {INV_EXTRA_DEFS.map(def =>
        checks[def.key] ? (
          <DataTable
            key={def.key}
            sectionId="assets"
            tableDef={{ ...investmentTables[def.key], reorderable: true, detailSubRow: true }}
          />
        ) : null
      )}

      {/* Free-text catch-all for anything the structured account tables don't
          capture — account numbers still to gather, held-away assets, pending
          transfers, advisor notes. Stored at assets.additionalAccountInfo. NOTE:
          the matching Salesforce field is pending (Srivani); until it lands this
          persists locally but will not round-trip on a Salesforce reload. */}
      <div className="grid" style={{ marginTop: 12 }}>
        <Field
          field={{ key: 'additionalAccountInfo', label: 'Additional Account Info', type: 'textarea' }}
          value={data?.assets?.additionalAccountInfo}
          onChange={val => update('assets.additionalAccountInfo', val)}
        />
      </div>
    </SectionShell>
  );
}
